import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { organizationAPI, employeeAPI, incrementAPI } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import ZohoIncrementsTable from '@/components/zoho/ZohoIncrementsTable';

export default function IncrementsPage() {
  const { isAdminLevel } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [increments, setIncrements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOrg, setFilterOrg] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newSalary, setNewSalary] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');
  const [selectedEmpId, setSelectedEmpId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [orgRes, empRes, incRes] = await Promise.all([
        organizationAPI.getAll(),
        employeeAPI.getAll(),
        incrementAPI.getAll(),
      ]);
      setOrganizations(orgRes.data);
      setEmployees(empRes.data);
      setIncrements(incRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.id === empId);
    setSelectedEmpId(empId);
    setSelectedEmployee(emp);
    setNewSalary('');
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) return;
    const currentSalary = selectedEmployee.gross_salary || selectedEmployee.monthly_salary || 0;
    const newSalaryNum = parseFloat(newSalary);
    if (newSalaryNum <= currentSalary) {
      toast.error('New salary must be greater than current salary');
      return;
    }
    setSubmitting(true);
    try {
      await incrementAPI.create({
        employee_id: selectedEmpId,
        previous_gross_salary: currentSalary,
        new_gross_salary: newSalaryNum,
        increment_amount: newSalaryNum - currentSalary,
        effective_date: effectiveDate,
        remarks: remarks || null,
      });
      toast.success('Increment applied successfully');
      setAddDialogOpen(false);
      setSelectedEmployee(null);
      setSelectedEmpId('');
      setNewSalary('');
      setRemarks('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create increment');
    } finally {
      setSubmitting(false);
    }
  };

  const getOrgName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return '-';
    const org = organizations.find(o => o.id === emp.organization_id);
    return org?.name || '-';
  };

  const calcPct = (prev, curr) => {
    if (!prev || prev === 0) return '0.0';
    return ((curr - prev) / prev * 100).toFixed(1);
  };

  const filteredData = increments.filter(inc => {
    if (filterOrg !== 'all') {
      const emp = employees.find(e => e.id === inc.employee_id);
      if (!emp || emp.organization_id !== filterOrg) return false;
    }
    const name = inc.employee_name || '';
    const code = inc.employee_code || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase()) || 
           code.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const currentSalaryDisplay = selectedEmployee 
    ? formatCurrency(selectedEmployee.gross_salary || selectedEmployee.monthly_salary || 0) 
    : '-';

  const totalIncrementAmount = increments.reduce((s, i) => s + (i.increment_amount || 0), 0);
  const uniqueEmployees = [...new Set(increments.map(i => i.employee_id))].length;

  return (
    <DashboardLayout>
      <div data-testid="increments-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Salary Increments</h1>
            <p className="text-muted-foreground mt-1">Manage employee salary increments and view history</p>
          </div>
          {isAdminLevel() && (
            <Button onClick={() => setAddDialogOpen(true)} data-testid="add-increment-btn">
              + New Increment
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Increments</p>
              <p className="text-2xl font-bold">{increments.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Employees with Increments</p>
              <p className="text-2xl font-bold">{uniqueEmployees}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Increment Amount</p>
              <p className="text-2xl font-bold font-mono">{formatCurrency(totalIncrementAmount)}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="zoho" className="space-y-4" data-testid="increments-tabs">
          <TabsList>
            <TabsTrigger value="zoho" data-testid="tab-zoho-increments">Zoho Increments</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual-increments">Manual Increments</TabsTrigger>
          </TabsList>

          <TabsContent value="zoho">
            <Card>
              <CardContent className="pt-6">
                <ZohoIncrementsTable />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="manual">

        <div className="flex flex-wrap gap-4">
          <Input 
            placeholder="Search by employee name or ID..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="max-w-sm"
          />
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {organizations.map(o => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Increment History</CardTitle>
            <CardDescription>All salary increments sorted by date</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <p className="text-muted-foreground">No increments recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead className="text-right">Previous Salary</TableHead>
                      <TableHead className="text-right">New Salary</TableHead>
                      <TableHead className="text-right">Increment</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map(inc => (
                      <TableRow key={inc.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inc.employee_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{inc.employee_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>{getOrgName(inc.employee_id)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {formatCurrency(inc.previous_gross_salary)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-emerald-600">
                          {formatCurrency(inc.new_gross_salary)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-emerald-600">+{formatCurrency(inc.increment_amount)}</span>
                          <Badge variant="secondary" className="ml-2 text-xs">
                            +{calcPct(inc.previous_gross_salary, inc.new_gross_salary)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(inc.effective_date)}</TableCell>
                        <TableCell className="max-w-[150px] truncate text-sm text-muted-foreground">
                          {inc.remarks || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

          </TabsContent>
        </Tabs>

        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>New Salary Increment</DialogTitle>
              <DialogDescription>Apply a salary increment to an employee</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Employee</Label>
                <Select value={selectedEmpId} onValueChange={handleEmployeeSelect}>
                  <SelectTrigger data-testid="increment-employee-select">
                    <SelectValue placeholder="Choose an employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedEmployee && (
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <p className="text-muted-foreground text-sm">Current Gross Salary</p>
                    <p className="text-xl font-bold font-mono text-amber-600">{currentSalaryDisplay}</p>
                  </CardContent>
                </Card>
              )}
              <div className="space-y-2">
                <Label>New Gross Salary</Label>
                <Input 
                  type="number" 
                  placeholder="50000" 
                  value={newSalary} 
                  onChange={(e) => setNewSalary(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input 
                  type="date" 
                  value={effectiveDate} 
                  onChange={(e) => setEffectiveDate(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Input 
                  placeholder="Annual appraisal, Promotion, etc." 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting || !selectedEmpId || !newSalary}>
                {submitting ? 'Applying...' : 'Apply Increment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
